// Quote-page Time & Materials helpers (pure). The server (jobq-api _tm_prepare_quote_lines) is the
// authority: it forces every T&M line to 0.00 / 'tm_pending' and refuses the line shapes below.
// These helpers only keep the page from offering what the server would refuse, and build the
// T&M part of the save payload — which is EMPTY for a fixed quote that stays fixed, so a fixed
// save sends byte-for-byte the payload it always did.

import { money2, toCents } from './tmPricing';
import { num } from './priceMath';
import { round2 } from './quotePricing';

export const TM_NOT_IN_V1 = 'Not available on T&M orders in v1';

export const TM_QUOTE_HELP =
  'Internal placeholder: no customer quote and no rate card. The order is priced on the Sales Order ' +
  'from its own shop time and material after it ships.';

export const TM_ACCEPT_CONFIRM =
  'Accept this quote as Time & Materials? No book prices will change: parts, assemblies and components ' +
  'keep their prices. Convert it to a Sales Order next; the order is priced there after it ships ' +
  '(Approve T&M Pricing).';

export const TM_RESTORE_CONFIRM =
  'Restore this T&M quote to Draft? Accepting it wrote no prices, so there is nothing to reverse.';

export const TM_POLICY_NOTE = 'T&M orders bill what ships (Ship Any Qty, Bill Shipped).';

export const TM_YIELD_POLICY_ID = '4';

const lineLabel = (l, i) => l.product_id || l.description || `line ${i + 1}`;

const isBlankAdvancedLine = (l) => !l.part_id && !l.consumable_id && !l.is_assembly_ops && !l._source_assembly_id
  && !String(l.product_id || '').trim() && !String(l.description || '').trim();

/**
 * Why these lines cannot be on a T&M quote (v1: one real part per line; no assemblies, operations,
 * purchased components, quick / text-only lines, or the same part twice). [] = fine.
 */
export function tmQuoteLineRefusals(lines, { quickMode = false, quickLines = [] } = {}) {
  const out = [];
  if (quickMode && (quickLines || []).some((l) => String(l.product_id || '').trim() || String(l.description || '').trim())) {
    out.push('Quick-quote lines cannot be T&M: add the real parts as Advanced lines.');
  }
  const seen = new Map();
  (lines || []).forEach((l, i) => {
    if (!l || isBlankAdvancedLine(l)) return;
    const label = lineLabel(l, i);
    if (l.is_assembly_ops) out.push(`${label}: assembly operations lines are not T&M in v1`);
    else if (l._source_assembly_id) out.push(`${label}: assembly lines are not T&M in v1`);
    else if (l.consumable_id || l.is_consumable_view) out.push(`${label}: purchased components are not T&M in v1`);
    else if (!l.part_id || l.is_quick_quote_item) out.push(`${label}: a T&M line needs a real part (no text-only lines)`);
    else if (seen.has(l.part_id)) out.push(`${label}: the same part is on two lines`);
    if (l.part_id) seen.set(l.part_id, true);
  });
  return out;
}

// A line as the Fixed → T&M switch leaves it: $0 on the 'tm_pending' basis, nothing else touched.
const asTmLine = (l) => ({ ...l, price: '0', price_source: 'tm_pending', price_basis: null, _price_source_inferred: false });

/** Fixed → T&M: drop blank rows, every line to $0 on the 'tm_pending' basis (the server does the same). */
export function toTmLines(lines) {
  return (lines || []).filter((l) => !isBlankAdvancedLine(l)).map(asTmLine);
}

/**
 * T&M → fixed: every line takes its calculated price (round2 of cost_breakdown.calculated_price)
 * on the 'calculated' basis. Lines with no calculated price are listed in `missing` ("Calc first").
 * A blank row has nothing to price and stays as it is. `keep(line)` may hand back the line to use
 * instead (restoreFixedLines: a line not changed since the switch); null = price it as above.
 */
export function toFixedLines(lines, { keep = () => null } = {}) {
  const missing = [];
  const out = (lines || []).map((l, i) => {
    const kept = keep(l);
    if (kept) return kept;
    if (!l || isBlankAdvancedLine(l)) return l;
    const calc = num(l.cost_breakdown && l.cost_breakdown.calculated_price, NaN);
    if (!Number.isFinite(calc) || calc <= 0) {
      missing.push(lineLabel(l, i));
      return l;
    }
    return { ...l, price: round2(calc).toFixed(2), price_source: 'calculated', price_basis: null, _price_source_inferred: false };
  });
  return { lines: out, missing };
}

// Equal as data (strings, numbers, booleans, null, arrays, plain objects), not by reference.
const sameData = (a, b) => {
  if (a === b || (Number.isNaN(a) && Number.isNaN(b))) return true;
  if (!a || !b || typeof a !== 'object' || typeof b !== 'object' || Array.isArray(a) !== Array.isArray(b)) return false;
  return [...new Set([...Object.keys(a), ...Object.keys(b)])].every((k) => sameData(a[k], b[k]));
};

// Screen state only (the breakdown's Details / Hide link): opening it is not an edit.
const VIEW_ONLY_KEYS = new Set(['show_breakdown']);

// True when `line` is still exactly what the Fixed → T&M switch made of `before`: every field,
// price and basis included, is as the switch left it (a quantity, description, Calc, revision or
// price picked while in T&M all count as a change).
const unchangedSinceSwitch = (line, before) => {
  const switched = asTmLine(before);
  return [...new Set([...Object.keys(line), ...Object.keys(switched)])]
    .every((k) => VIEW_ONLY_KEYS.has(k) || sameData(line[k], switched[k]));
};

/**
 * T&M → fixed straight after a Fixed → T&M on a quote stored as fixed (no save in between).
 * `before` = the fixed lines at the switch, `lines` = the lines on screen now. Matched by line id:
 *   - a line not changed since the switch gets its fixed price, price source, price basis and
 *     inferred flag back (the fixed line itself);
 *   - a line changed or added while in T&M goes through toFixedLines (its calculated price on the
 *     'calculated' basis, or listed in `missing` = "Calc first");
 *   - a line removed while in T&M stays removed.
 * `untouched` = nothing but the switch itself changed the lines: `lines` is then `before` exactly
 * (its blank rows included), so the page may also put back the Quick / Advanced mode.
 * Returns {lines, missing, untouched}.
 */
export function restoreFixedLines(before, lines) {
  const beforeById = new Map();
  (before || []).forEach((b) => { if (b && b.id != null && !beforeById.has(b.id)) beforeById.set(b.id, b); });
  const kept = new Map(); // fixed line (before) → the line restored from it
  const keep = (l) => {
    const b = l && l.id != null ? beforeById.get(l.id) : undefined;
    if (!b || kept.has(b) || !unchangedSinceSwitch(l, b)) return null;
    const restored = sameData(l.show_breakdown, b.show_breakdown) ? b : { ...b, show_breakdown: l.show_breakdown };
    kept.set(b, restored);
    return restored;
  };
  const { lines: out, missing } = toFixedLines(lines, { keep });
  const live = (before || []).filter((b) => b && !isBlankAdvancedLine(b));
  const untouched = out.length === live.length && live.every((b, i) => kept.has(b) && out[i] === kept.get(b));
  return {
    lines: untouched ? (before || []).map((b) => kept.get(b) || b) : out,
    missing,
    untouched,
  };
}

/** Estimated unit ($) of a T&M line: the engine's calculated price at the time, or null. */
export function tmLineEstimateUnit(line) {
  const calc = num(line && line.cost_breakdown && line.cost_breakdown.calculated_price, NaN);
  return Number.isFinite(calc) ? round2(calc) : null;
}

/** Σ estimated unit × qty over the lines that have an estimate (the server's default estimate). */
export function tmLinesEstimate(lines) {
  let total = 0;
  let known = 0;
  (lines || []).forEach((l) => {
    const u = tmLineEstimateUnit(l);
    if (u == null) return;
    known += 1;
    total += u * (parseInt(l.quantity, 10) || 0);
  });
  return { total: round2(total), known, count: (lines || []).length };
}

/**
 * The T&M keys of the quote save payload.
 *   fixed quote staying fixed         → {}                (payload unchanged)
 *   T&M quote switched back to fixed  → {pricing_mode: 'fixed'}
 *   T&M                               → {pricing_mode: 'tm'} + tm_estimate_total / tm_not_to_exceed
 *                                       ONLY when typed (dirty): a typed value as a 2-dp string, a
 *                                       cleared box as null (the server then defaults the estimate).
 *                                       Untouched = omitted = the server keeps / defaults it.
 * Returns {fields, errors}.
 */
export function tmQuoteSaveFields({ mode, storedMode, estimate, nte, dirty = {} }) {
  const errors = [];
  if (mode !== 'tm') {
    return { fields: storedMode === 'tm' ? { pricing_mode: 'fixed' } : {}, errors };
  }
  const fields = { pricing_mode: 'tm' };
  const typed = (key, raw, label) => {
    if (!dirty[key]) return;
    const s = raw === null || raw === undefined ? '' : String(raw).trim();
    if (!s) { fields[key] = null; return; }
    const v = money2(s);
    if (v === null || toCents(v) < 0) { errors.push(`${label}: type an amount like 1250.00`); return; }
    fields[key] = v;
  };
  typed('tm_estimate_total', estimate, 'Internal estimate');
  typed('tm_not_to_exceed', nte, 'Not-to-exceed');
  return { fields, errors };
}
